import { supabase } from './supabaseClient';

/**
 * Save push subscription to Supabase
 */
export const savePushSubscription = async (userEmail, subscription) => {
  try {
    const { data, error } = await supabase
      .from('push_subscriptions')
      .upsert({
        user_email: userEmail,
        subscription: JSON.stringify(subscription),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error saving push subscription:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get push subscriptions for user(s)
 */
export const getPushSubscriptions = async (userEmails) => {
  try {
    const emails = Array.isArray(userEmails) ? userEmails : [userEmails];
    
    const { data, error } = await supabase
      .from('push_subscriptions')
      .select('*')
      .in('user_email', emails);

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching push subscriptions:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Send browser push notification
 */
export const sendBrowserPush = async (userEmail, notification) => {
  try {
    // Get user's push subscription
    const result = await getPushSubscriptions(userEmail);
    if (!result.success || result.data.length === 0) {
      console.log('No push subscription found for:', userEmail);
      return { success: false, error: 'No subscription' };
    }

    const subscription = JSON.parse(result.data[0].subscription);

    // Send via Netlify function
    const response = await fetch('/.netlify/functions/send-push-notification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscription,
        title: notification.title,
        body: notification.message,
        icon: notification.icon || 'https://alera.co.nz/alera-logo.png',
        url: notification.url || 'https://alera.co.nz/community-forum',
        tag: notification.tag || 'alera-notification'
      })
    });

    if (!response.ok) {
      // If subscription expired (410), remove it
      if (response.status === 410) {
        await supabase
          .from('push_subscriptions')
          .delete()
          .eq('user_email', userEmail);
      }
      throw new Error(`Push failed: ${response.status}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error sending browser push:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new notification for a user
 */
export const createNotification = async (userEmail, notificationData) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .insert([{
        user_email: userEmail,
        type: notificationData.type,
        title: notificationData.title,
        message: notificationData.message,
        link: notificationData.link || null,
        post_id: notificationData.postId || null
      }])
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get notifications for a user
 */
export const getNotifications = async (userEmail, unreadOnly = false) => {
  try {
    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_email', userEmail)
      .order('created_at', { ascending: false })
      .limit(50);

    if (unreadOnly) {
      query = query.eq('is_read', false);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching notifications:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Mark notification(s) as read
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId)
      .select();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark all notifications as read for a user
 */
export const markAllNotificationsAsRead = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_email', userEmail)
      .eq('is_read', false)
      .select();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a notification
 */
export const deleteNotification = async (notificationId) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get notification preferences for a user
 */
export const getNotificationPreferences = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_email', userEmail)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    
    // Return default preferences if none exist
    return { 
      success: true, 
      data: data || {
        email_on_new_post: true,
        email_on_post_like: true,
        email_on_comment: true,
        email_on_admin_post: true
      }
    };
  } catch (error) {
    console.error('Error fetching notification preferences:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update notification preferences
 */
export const updateNotificationPreferences = async (userEmail, preferences) => {
  try {
    const { data, error } = await supabase
      .from('notification_preferences')
      .upsert({
        user_email: userEmail,
        ...preferences,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating notification preferences:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send notification to all members about a new post
 */
export const notifyMembersOfNewPost = async (postData) => {
  try {
    const response = await fetch('/.netlify/functions/forum-notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'new_post',
        postId: postData.postId,
        authorEmail: postData.authorEmail,
        authorName: postData.authorName,
        category: postData.category,
        preview: postData.preview,
        isAdmin: postData.isAdmin
      })
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to send notifications');
    return { success: true, notified: result.notified };
  } catch (error) {
    console.error('Error notifying members:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Notify post author when their post gets a like
 */
export const notifyPostLike = async (postAuthorEmail, likerName, postId, postTitle) => {
  try {
    const likerEmail = localStorage.getItem('userEmail');
    if (likerEmail === postAuthorEmail) return { success: true };

    const response = await fetch('/.netlify/functions/forum-notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'like',
        postAuthorEmail,
        likerName,
        likerEmail,
        postId,
        postTitle
      })
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to send like notification');
    return { success: true };
  } catch (error) {
    console.error('Error notifying post like:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Notify post author when someone comments on their post
 */
export const notifyPostComment = async (postAuthorEmail, commenterName, postId, postTitle, commentPreview) => {
  try {
    const commenterEmail = localStorage.getItem('userEmail');
    if (commenterEmail === postAuthorEmail) return { success: true };

    const response = await fetch('/.netlify/functions/forum-notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'comment',
        postAuthorEmail,
        commenterName,
        commenterEmail,
        postId,
        postTitle,
        commentPreview
      })
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Failed to send comment notification');
    return { success: true };
  } catch (error) {
    console.error('Error notifying post comment:', error);
    return { success: false, error: error.message };
  }
};
